import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  useLayoutEffect,
} from 'react';
import { View, Text, Platform, LayoutChangeEvent } from 'react-native';
import {
  DocketStatus,
  LayoutStrategy,
  PrinterProfile,
} from '@oolio-group/domain';
import { useTranslation } from '@oolio-group/localization';
import { useScale } from '../../../hooks/useScale';
import { useDockets } from '../../../hooks/useDockets';
import { useReactiveVar } from '@apollo/client/react/hooks';
import {
  docketsUpdated,
  openDocketslastUpdatedAt,
  docketsLastMovedAt,
  allDayViewItemsVar,
  rushedVar,
} from '../../../state/apolloVars';
import { PaginatedDocketItems } from '../../../utils/layoutHelper';
import { headers } from '../../../utils/fetchHeaders';
import { transformDocketToDocketDisplayItems } from '../../../utils/docketHelper/transformDockets';
import {
  DOCKET_WIDTH,
  FEATURES,
  FooterHeight,
  HeaderHeight,
  SortingPreference,
} from '../../../common/constants';
import { DocketDisplayItem, DocketDetailsProps } from '../../../common/types';
import MainScreenStyles from '../MainScreen.styles';
import DocketsGrid from './DocketsGrid';
import DocketHermes from './DocketHermes';
import Pagination from '../../../component/Pagination/Pagination';
import { usePrinterProfiles } from '../../../hooks/usePrinterProfiles';
import { useOrderTypes } from '../../../hooks/useOrderTypes';
import { useSalesChannels } from '../../../hooks/useSalesChannels';
import { useSession } from '../../../hooks/useSession';
import { groupDocketItems } from '../../../utils/docketHelper/groupDocketItems';
import {
  getItemCounts,
  sortDockets,
} from '../../../utils/docketHelper/sortDockets';
import { docketsCategoriesCount } from '../../../state/apolloVars';
import { analyticsService } from '../../../analytics/AnalyticsService';
import theme from '../../../common/theme';
import Svg, { Line } from 'react-native-svg';
import { getTwoRowPaginatedDocketItems } from '../../../utils/layouts/TwoRow/paginatedDocketItems';
import { getSingleRowPaginatedDocketItems } from '../../../utils/layouts/SingleRow/paginatedDocketItems';
import { getPaginatedDocketItems } from '../../../utils/layouts/FreeFlow/paginatedDocketItems';
import { addNotifierToDockets } from '../../../utils/docketHelper/notifierDockets';
import { consolidateDockets } from '../../../utils/docketHelper/consolidateDockets';
import { sortDocketItems } from '../../../utils/docketItemHelper/sortDocketItems';
import { useCategories } from '../../../hooks/useCategories';

export interface DocketsLayoutProps {
  docketStatus: DocketStatus | string;
  headers: headers;
  setCurrentPage: (page: number) => void;
  currentPage: number;
  enablePrint?: boolean;
  onToggleDocketDetails: (a: DocketDetailsProps) => void;
  setDocketDetails: (a: DocketDetailsProps) => void;
  selectedDocket?: string;
}

const DocketsLayout: React.FC<DocketsLayoutProps> = ({
  docketStatus,
  headers,
  setCurrentPage: setCurrentPageIndex,
  currentPage,
  enablePrint,
  onToggleDocketDetails,
  setDocketDetails,
  selectedDocket,
}) => {
  const { scale } = useScale();
  const { session } = useSession();
  const styles = MainScreenStyles();
  const { translate } = useTranslation();
  const { getDockets, ackDocketsReceived } = useDockets();
  const [layoutHeight, setLayoutHeight] = useState(0);
  const docketsUpdatedVar = useReactiveVar(openDocketslastUpdatedAt);
  const docketMoved = useReactiveVar(docketsLastMovedAt);
  const previousDocketItemsRef = useRef<PaginatedDocketItems[]>([]);
  const sortingPrefRef = useRef('');
  const [docketsToShow, setDocketsToShow] = useState<DocketDisplayItem[]>([]);
  const [paginatedDocketItems, setPaginatedDocketItems] = useState<
    PaginatedDocketItems[]
  >([]);
  const { printerProfiles } = usePrinterProfiles();
  const { orderTypes } = useOrderTypes();
  const { salesChannels } = useSalesChannels();
  const { categories } = useCategories();
  const [columnHeight, setColumnHeight] = useState<number>(0);
  const [docketsReceivedAckEnabled, setDocketsReceivedAckEnabled] =
    useState(false);
  const isRushed = rushedVar();

  const assignedProfiles = useMemo(
    () =>
      session?.kitchenDisplay?.printerProfiles?.map((x: PrinterProfile) => x) ||
      [],
    [session?.kitchenDisplay?.printerProfiles],
  );
  useEffect(() => {
    (async () => {
      const isEnabled = await analyticsService.isFeatureEnabled(
        FEATURES.DOCKETS_RECEIVED_ACK,
      );
      setDocketsReceivedAckEnabled(isEnabled);
    })();
  }, []);

  const getOrderTypeCode = useCallback(
    (id: string) => {
      return orderTypes.find(orderType => orderType?.id === id)?.code as string;
    },
    [orderTypes],
  );

  const assignedProfilesIds = useMemo(
    () =>
      Array.from(
        new Set(
          session?.kitchenDisplay?.printerProfiles?.map(
            (p: PrinterProfile) => p.id,
          ) || [],
        ),
      ),
    [session?.kitchenDisplay?.printerProfiles],
  );

  useEffect(() => {
    (async () => {
      if (
        !isNaN(docketsUpdatedVar) &&
        orderTypes.length &&
        salesChannels.length &&
        categories.length
      ) {
        const dockets = await getDockets(docketStatus as DocketStatus);
        const filteredDockets = dockets.filter(x => {
          const validOrderTypes = printerProfiles.find(
            profile => profile.id === x?.printerProfileId,
          )?.orderTypes;
          if (!validOrderTypes || validOrderTypes?.length === 0) return x;
          if (validOrderTypes.includes(getOrderTypeCode(x?.orderType?.id)))
            return x;
          return;
        });
        if (docketStatus !== DocketStatus.COMPLETED) {
          const allDayViewData = groupDocketItems(filteredDockets);
          allDayViewItemsVar(allDayViewData);
        }
        const sortedDockets = sortDockets(
          session.sortingPref?.value || '',
          filteredDockets,
          docketStatus,
          orderTypes,
          salesChannels,
        );
        const itemCounts = getItemCounts(sortedDockets, assignedProfilesIds);
        if (itemCounts) docketsCategoriesCount(itemCounts);
        if (
          sortingPrefRef.current !==
          (session?.sortingPref?.value ?? SortingPreference.OLDEST_FIRST)
        ) {
          previousDocketItemsRef.current = [];
          sortingPrefRef.current =
            session?.sortingPref?.value ?? SortingPreference.OLDEST_FIRST;
        }
        const docketsWithSortedItems = sortDocketItems(
          sortedDockets,
          assignedProfiles,
          categories,
        );
        const consolidatedDockets = consolidateDockets(
          docketsWithSortedItems,
          printerProfiles,
        );
        // const notifiersDockets = addNotifierToDockets(
        //   consolidatedDockets,
        //   printerProfiles,
        //   assignedProfiles,
        // );
        const transformedDockets = consolidatedDockets.map(docket => {
          return transformDocketToDocketDisplayItems(
            docket,
            assignedProfiles,
            session,
            orderTypes,
            categories,
          );
        });
        const dataToShow = transformedDockets?.flat();
        setDocketsToShow(dataToShow);
        docketsReceivedAckEnabled && ackDocketsReceived(sortedDockets);
      }
    })();
  }, [
    getDockets,
    docketsUpdatedVar,
    categories,
    docketStatus,
    session,
    printerProfiles,
    getOrderTypeCode,
    session.sortingPref,
    orderTypes,
    salesChannels,
    ackDocketsReceived,
    docketsReceivedAckEnabled,
    assignedProfiles,
    assignedProfilesIds,
  ]);

  const availableHeight = useMemo(() => {
    if (Platform.OS === 'web') {
      return theme.window.vh - scale(FooterHeight) - scale(HeaderHeight);
    }
    return theme.window.vh - 190;
  }, [scale]);

  const getPaginatedItems = useCallback(
    (
      strategy: LayoutStrategy | undefined,
      dockets: DocketDisplayItem[],
      docketWidth: number,
      scale: (n: number) => number,
      height: number,
      columnsOnScreen: number,
      sortingPref?: string,
    ) => {
      // Default to FREE_FLOW if strategy is undefined
      const layoutStrategy = strategy || LayoutStrategy.FREE_FLOW;

      switch (layoutStrategy) {
        case LayoutStrategy.DOUBLE_ROW:
          return getTwoRowPaginatedDocketItems(
            dockets,
            docketWidth,
            scale,
            height,
            previousDocketItemsRef.current,
            columnsOnScreen,
            docketStatus === DocketStatus.CREATED
              ? sortingPref
              : SortingPreference.OLDEST_FIRST,
          );

        case LayoutStrategy.SINGLE_ROW:
          return getSingleRowPaginatedDocketItems(
            dockets,
            docketWidth,
            scale,
            height,
            columnsOnScreen,
          );

        default:
          return getPaginatedDocketItems(
            dockets,
            docketWidth,
            scale,
            height,
            columnsOnScreen,
          );
      }
    },
    [docketStatus],
  );

  useLayoutEffect(() => {
    if (docketsToShow.length) {
      setPaginatedDocketItems(() =>
        getPaginatedItems(
          session?.kitchenDisplay?.layoutStrategy?.strategy,
          docketsToShow,
          scale(DOCKET_WIDTH),
          scale,
          layoutHeight > 0 ? layoutHeight : availableHeight,
          session?.kitchenDisplay?.layoutStrategy?.columnsOnScreen ?? 8,
          session?.sortingPref?.value,
        ),
      );
    } else {
      setPaginatedDocketItems([]);
    }
  }, [
    docketsToShow,
    scale,
    columnHeight,
    session?.kitchenDisplay?.layoutStrategy?.strategy,
    session?.kitchenDisplay?.layoutStrategy?.columnsOnScreen,
    availableHeight,
    layoutHeight,
    session?.sortingPref?.value,
    getPaginatedItems,
  ]);

  const onChangePage = useCallback(
    (page: number) => {
      setCurrentPageIndex(page);
    },
    [setCurrentPageIndex],
  );
  useEffect(() => {
    previousDocketItemsRef.current = [];
  }, [
    session?.sortingPref?.value,
    session?.kitchenDisplay?.layoutStrategy,
    scale,
    isRushed,
    docketMoved,
  ]);

  useEffect(() => {
    onChangePage(0);
  }, [
    session?.kitchenDisplay?.layoutStrategy?.columnsOnScreen,
    onChangePage,
    session?.kitchenDisplay?.layoutStrategy?.strategy,
  ]);

  useEffect(() => {
    if (paginatedDocketItems.length > 0) {
      previousDocketItemsRef.current = paginatedDocketItems;
    }
  }, [paginatedDocketItems]);

  useEffect(() => {
    docketsUpdated({ timestamp: Date.now() });
  }, []);

  const dividerTop =
    Platform.OS === 'android'
      ? availableHeight ?? 'auto'
      : columnHeight ?? 'auto';

  const computedTop = typeof dividerTop === 'number' ? dividerTop / 2 : 'auto';

  const onLayout = (event: LayoutChangeEvent) => {
    const { height } = event.nativeEvent.layout;
    setLayoutHeight?.(height - scale(FooterHeight));
  };

  return (
    <View style={[styles.layout, styles.container]} onLayout={onLayout}>
      {paginatedDocketItems.length > 0 ? (
        <>
          {session?.kitchenDisplay?.layoutStrategy?.strategy ===
            LayoutStrategy.DOUBLE_ROW && (
            <View
              style={[
                styles.divider,
                {
                  top: computedTop,
                },
              ]}
            >
              <Svg height="4" width="100%">
                <Line
                  x1="0"
                  y1="0"
                  x2="100%"
                  y2="0"
                  stroke={theme.colors.grey6}
                  strokeWidth="4"
                  strokeDasharray="4, 4"
                />
              </Svg>
            </View>
          )}
          <DocketsGrid
            status={docketStatus as DocketStatus}
            displayDocketItems={paginatedDocketItems}
            currentPage={currentPage}
            enablePrint={enablePrint}
            setColumnsHeight={setColumnHeight}
            columnsHeight={columnHeight}
            onToggleDocketDetails={onToggleDocketDetails}
            setDocketDetails={setDocketDetails}
            selectedDocket={selectedDocket}
          />

          <Pagination
            data={paginatedDocketItems.map(x => x.page)}
            currentPageIndex={currentPage}
            onChange={onChangePage}
          />
        </>
      ) : (
        <View style={styles.emptyView}>
          {docketStatus === DocketStatus.CREATED ? (
            <></>
          ) : (
            <Text style={styles.emptyText}>
              {translate('kitchenDisplay.emptyLayout')}
            </Text>
          )}
        </View>
      )}
      <DocketHermes
        headers={headers}
        docketStatus={docketStatus as DocketStatus}
      />
    </View>
  );
};

export default DocketsLayout;
